import { ReactNode, useId } from "react";
import { Check } from "lucide-react";
import { Input, type InputProps } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ValidationState } from "./useFieldValidation";
import { ErrorIcon } from "@/components/icons/status";

interface ValidatedFieldProps extends Omit<InputProps, "trailing"> {
  label: string;
  /** Action à droite du label (ex: lien « mot de passe oublié »). */
  labelAction?: ReactNode;
  /** Erreur prioritaire (post-submit serveur). */
  errorOverride?: string;
  validation?: { state: ValidationState; message?: string };
  /** Élément trailing supplémentaire (ex: toggle œil) — placé avant l'icône de statut. */
  trailing?: ReactNode;
  hideStatusIcon?: boolean;
}

export function ValidatedField({
  label,
  labelAction,
  errorOverride,
  validation,
  trailing,
  hideStatusIcon,
  id: providedId,
  ...inputProps
}: ValidatedFieldProps) {
  const reactId = useId();
  const id = providedId ?? `field-${reactId}`;
  const errorMessage = errorOverride ?? (validation?.state === "invalid" ? validation.message : undefined);
  const isValid = !errorOverride && validation?.state === "valid";
  const isInvalid = Boolean(errorMessage);

  const statusIcon = hideStatusIcon
    ? null
    : isInvalid ? (
        <span className="text-destructive" aria-hidden="true">
          <ErrorIcon className="w-4 h-4" />
        </span>
      ) : isValid ? (
        <span className="text-success" aria-hidden="true">
          <Check className="w-4 h-4" strokeWidth={3} />
        </span>
      ) : null;

  const composedTrailing =
    trailing || statusIcon ? (
      <span className="flex items-center gap-1.5 pr-2.5">
        {trailing}
        {statusIcon}
      </span>
    ) : undefined;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="text-sm font-medium text-foreground/90">
          {label}
        </Label>
        {labelAction}
      </div>
      <Input
        id={id}
        variant="premium"
        aria-invalid={isInvalid || undefined}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
        className={cn(isValid && "border-success/60 focus:border-success focus:ring-success/15")}
        trailing={composedTrailing}
        {...inputProps}
      />
      {errorMessage && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-xs text-destructive flex items-center gap-1 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-1 duration-fast"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}
